import type { AgentRequest } from '../store/agenticHubSlice'

/**
 * Normalize raw /requests API response rows into AgentRequest objects consumed by the Redux slice.
 *
 * Shared between the useAgenticHub hook (live refresh) and the replay handler (stale fallback on
 * reconnect). Identical parsing ensures both paths produce structurally equivalent Redux state.
 */
export function parseRawRequests(rawRequests: Record<string, unknown>[]): AgentRequest[] {
    return rawRequests.map((request) => {
        const options = Array.isArray(request.options_json)
            ? request.options_json.reduce<AgentRequest['options']>((acc, option) => {
                  if (!option || typeof option !== 'object') return acc
                  const normalized = option as Record<string, unknown>
                  const id = String(normalized.id ?? '').trim()
                  const label = String(normalized.label ?? '').trim()
                  if (!id || !label) return acc
                  acc.push({
                      id,
                      label,
                      description: typeof normalized.description === 'string' ? normalized.description : undefined,
                      recommended: normalized.recommended === true,
                      style: typeof normalized.style === 'string' ? normalized.style : undefined,
                  })
                  return acc
              }, [])
            : []

        const meta = (request.metadata_json as Record<string, unknown> | null) ?? null
        return {
            id: String(request.id ?? request._id ?? ''),
            request_type: (request.request_type === 'approval' ? 'approval' : 'question') as 'question' | 'approval',
            status: ((request.status as AgentRequest['status']) || 'pending') as AgentRequest['status'],
            surface: ((request.surface as string) === 'agent' || (request.surface as string) === 'email' ? 'agent' : 'chat') as AgentRequest['surface'],
            title: String(request.title ?? ''),
            body: String(request.body ?? ''),
            options,
            // Match the live-socket create-branch default (`allow_note === undefined ? true : Boolean(...)`).
            // BE may omit the field on chat-agent `ask_questions` rows; treating
            // absent as `false` here flipped the "Other" free-text affordance
            // off purely on page reload — same row showed it live and hid it
            // after refresh.
            allow_note: request.allow_note === undefined ? true : Boolean(request.allow_note),
            allow_edit: Boolean(meta?.allowEdit),
            multi_select: Boolean(meta?.multi_select),
            resource_keys: (() => {
                const keys = Array.isArray(request.resource_keys) ? request.resource_keys.map((value) => String(value)) : []
                // Fallback: extract postingId from metadata if resource_keys is empty (legacy requests)
                if (keys.length === 0 && meta?.postingId) keys.push(String(meta.postingId))
                return keys
            })(),
            created_at: request.created_at as string | undefined,
            updated_at: request.updated_at as string | undefined,
            resolved_at: (request.resolved_at as string | null | undefined) ?? null,
            archived_load: Boolean(request.archived_load),
            metadata_type: typeof meta?.type === 'string' ? (meta.type as string) : undefined,
            correlation_id: typeof meta?.correlationId === 'string' ? (meta.correlationId as string) : undefined,
            question_index: typeof meta?.questionIndex === 'number' ? (meta.questionIndex as number) : undefined,
            questions_total: typeof meta?.questionsTotal === 'number' ? (meta.questionsTotal as number) : undefined,
        }
    })
}
